import type { TextFieldPropValue } from 'components/FieldNumberFormat';
import type { WorkOrderPriceRatioRowData } from 'gql/graphql';
import { Button, Checkbox, cnMixSpace, IconExpand } from 'uikit';
import { formatNumberToRu } from 'utils';

import type { ColDef } from 'ag-grid-community';
import type React from 'react';
import { useCallback, useMemo } from 'react';

import { NoteCellRenderer } from '../NoteCellRenderer';

interface IGridPickCoefficientsColumns {
    displayRatioInfoHandler: (data: WorkOrderPriceRatioRowData) => () => void;
    setCheckedIds: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    checkedIds: Record<string, boolean>;
    isWorkOrderChange?: boolean;
    handleChangesValueNote: (rowId: number) => ({ value }: { value: TextFieldPropValue }) => void;
    valueNote: Record<string, string>;
}

export const useGridPickCoefficientsColumns = (props: IGridPickCoefficientsColumns) => {
    const { displayRatioInfoHandler, setCheckedIds, checkedIds, isWorkOrderChange, handleChangesValueNote, valueNote } =
        props;

    const handleChangeCheckbox = useCallback(
        (rowId: number) =>
            ({ checked }: { checked: boolean }) => {
                if (rowId) {
                    setCheckedIds((ids: any) => ({ ...ids, [rowId]: checked }));
                }
            },
        [setCheckedIds],
    );

    const columnDefs = useMemo<ColDef[]>(
        () => [
            {
                headerName: '',
                field: 'checkbox',
                width: 80,
                headerClass: 'firstColumn',
                cellClass: 'firstColumn',
                spanHeaderHeight: true,
                resizable: false,
                autoHeight: true,
                cellRenderer: ({ data }: { data: WorkOrderPriceRatioRowData }) => {
                    if (!data) {
                        return '';
                    }

                    return (
                        <>
                            <Checkbox checked={checkedIds[data.id]} onChange={handleChangeCheckbox(data?.id)} />
                            <Button
                                onlyIcon
                                iconLeft={IconExpand}
                                view="clear"
                                size="xs"
                                iconSize="xs"
                                className={cnMixSpace({
                                    mL: '2xs',
                                })}
                                onClick={displayRatioInfoHandler(data)}
                            />
                        </>
                    );
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 104,
                cellRenderer: ({ value }: { value: string }) => value || '',
            },
            {
                headerName: 'Наименование',
                field: 'workConditions',
                flex: 1,
                minWidth: 150,
                spanHeaderHeight: true,
                cellRenderer: ({ value }: { value: string }) => value || '',
            },
            {
                headerName: 'Вид работ',
                field: 'groupName',
                width: 160,
                spanHeaderHeight: true,
                cellRenderer: ({ value }: { value: string }) => value || '',
            },
            {
                headerName: 'К озп',
                field: 'kozp',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К эмм',
                field: 'kem',
                align: 'center',
                autoHeight: true,
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К тз',
                field: 'ktz',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'Ктз зпм',
                field: 'ktzzpm',
                autoHeight: true,
                align: 'center',
                width: 100,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К мат',
                autoHeight: true,
                field: 'kmat',
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'Кнр',
                field: 'knr',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'Ксп',
                field: 'ksp',
                align: 'center',
                autoHeight: true,
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            ...(isWorkOrderChange
                ? [
                      {
                          headerName: 'Примечание',
                          field: 'note',
                          autoHeight: true,
                          width: 160,
                          cellRenderer: NoteCellRenderer,
                          cellRendererParams: {
                              checkedIds,
                              valueNote,
                              handleChangesValueNote,
                          },
                      },
                  ]
                : []),
        ],
        [
            checkedIds,
            displayRatioInfoHandler,
            handleChangeCheckbox,
            handleChangesValueNote,
            isWorkOrderChange,
            valueNote,
        ],
    );

    return {
        columnDefs,
        checkedIds,
    };
};
